/* =========================================================================
   Accessibility helpers (keyboard + focus management)
   - BzA11y.getFocusable(root)      : visible, tabbable elements inside root
   - BzA11y.trapFocus(el, options)  : keep Tab / Shift+Tab inside a dialog
   - BzA11y.releaseFocus(el, opts)  : remove the trap and return focus
   - [role="button"] on non-native elements: Enter / Space activate it
   - Product thumbnails: Enter / Space show that media in the main gallery
   ========================================================================= */
(() => {
  if (window.BzA11y) return;

  const FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'summary',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const isVisible = (el) => {
    if (!el || el.closest('[inert], [hidden]')) return false;
    if (!el.getClientRects().length) return false;
    return getComputedStyle(el).visibility !== 'hidden';
  };

  // Radio groups take one tab stop: the checked radio, or the first one when none is checked.
  const isRadioTabStop = (el, list) => {
    if (el.type !== 'radio' || !el.name) return true;
    const group = list.filter((item) => item.type === 'radio' && item.name === el.name && item.form === el.form);
    const checked = group.find((item) => item.checked);
    return checked ? el === checked : el === group[0];
  };

  const getFocusable = (root) => {
    if (!root) return [];
    const list = [...root.querySelectorAll(FOCUSABLE)].filter((el) => el.tabIndex >= 0 && isVisible(el));
    return list.filter((el) => isRadioTabStop(el, list));
  };

  const focusEl = (el) => {
    if (!el || typeof el.focus !== 'function') return;
    el.focus({ preventScroll: true });
  };

  /* ---------- Focus trap (stack: the newest dialog wins) ---------- */
  const stack = [];

  const releaseFocus = (container, { restore = true, returnTo } = {}) => {
    const index = stack.findIndex((entry) => entry.container === container);
    if (index === -1) return;
    const [entry] = stack.splice(index, 1);
    clearTimeout(entry.timer);
    if (!restore) return;

    // Only move focus back when it is still inside the closed dialog (or lost to <body>)
    const active = document.activeElement;
    if (active && active !== document.body && container.isConnected && !container.contains(active)) return;

    const target = returnTo || entry.returnTo;
    if (target && target.isConnected && isVisible(target)) focusEl(target);
  };

  const trapFocus = (container, { initial, returnTo, delay = 60 } = {}) => {
    if (!container) return;
    releaseFocus(container, { restore: false });

    const entry = { container, returnTo: returnTo || document.activeElement };
    stack.push(entry);

    if (initial === false) return;
    entry.timer = setTimeout(() => {
      if (!stack.includes(entry)) return;
      if (container.contains(document.activeElement) && document.activeElement !== container) return;
      let target = initial || getFocusable(container)[0];
      if (!target) {
        if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
        target = container;
      }
      focusEl(target);
    }, delay);
  };

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !stack.length || event.defaultPrevented) return;
    const entry = stack[stack.length - 1];
    const { container } = entry;

    if (!container.isConnected || !isVisible(container)) {
      stack.pop();
      return;
    }

    const items = getFocusable(container);
    const active = document.activeElement;

    if (!items.length) {
      event.preventDefault();
      focusEl(container);
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];

    if (!container.contains(active)) {
      event.preventDefault();
      focusEl(event.shiftKey ? last : first);
    } else if (event.shiftKey && (active === first || active === container)) {
      event.preventDefault();
      focusEl(last);
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      focusEl(first);
    }
  });

  /* ---------- Product thumbnails: Enter / Space ---------- */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const thumb = event.target;
    if (!(thumb instanceof HTMLElement) || !thumb.matches('.swiper-thumbs .swiper-slide')) return;

    const host = thumb.closest('bz-product-media, bz-slider') || document;
    const mainEl = [...host.querySelectorAll('.swiper')].find(
      (el) => el.swiper && !el.classList.contains('swiper-thumbs') && !el.contains(thumb)
    );
    const main = mainEl?.swiper;
    if (!main) return;

    event.preventDefault();
    // Match by media id first (variant filtering can hide slides), then by position
    const slides = [...main.slides];
    const id = thumb.dataset.mediaId;
    let index = id ? slides.findIndex((slide) => slide.dataset.mediaId === id) : -1;
    if (index < 0) {
      const thumbs = [...thumb.parentElement.children].filter((el) => el.classList.contains('swiper-slide'));
      index = thumbs.indexOf(thumb);
    }
    if (index < 0) return;
    if (main.params.loop) main.slideToLoop(Number(slides[index]?.dataset.swiperSlideIndex ?? index));
    else main.slideTo(index);
  });

  /* ---------- [role="button"] on non-native elements ----------
     Registered on window, so it runs after every element / document handler.
     Elements that already handle the key call preventDefault() and are skipped. */
  const NATIVE = 'button, a[href], input, select, textarea, summary, [contenteditable="true"]';
  const SWIPER_CONTROLS = '.swiper-button-next, .swiper-button-prev, .swiper-pagination-bullet';

  window.addEventListener('keydown', (event) => {
    if (event.defaultPrevented || event.repeat) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const el = event.target;
    if (!(el instanceof HTMLElement) || el.getAttribute('role') !== 'button') return;
    if (el.matches(NATIVE) || el.matches(SWIPER_CONTROLS)) return; // Swiper's a11y module handles its own controls
    if (el.getAttribute('aria-disabled') === 'true') return;
    event.preventDefault();
    el.click();
  });

  /* ---------- Focus lands on content that is not on screen yet ----------
     - scroll-reveal sections start at opacity 0: show them as soon as focus enters
     - a focused control inside a scroll box (variant panel, search results, drawers) is
       scrolled into the middle of that box; visually hidden radios use their label */
  const scrollParent = (el) => {
    for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
      const style = getComputedStyle(node);
      const y = /(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1;
      const x = /(auto|scroll)/.test(style.overflowX) && node.scrollWidth > node.clientWidth + 1;
      if (x || y) return { node, x, y };
    }
    return null;
  };

  const visualTarget = (el) => {
    if (el.matches('input[type="radio"], input[type="checkbox"]')) {
      const rect = el.getBoundingClientRect();
      if (rect.width <= 2 || rect.height <= 2) return el.labels?.[0] || el.nextElementSibling || el;
    }
    return el;
  };

  const bringIntoView = (el) => {
    const target = visualTarget(el);
    const parent = scrollParent(target);
    if (!parent) {
      if (target !== el) target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      return;
    }
    const box = parent.node.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    const pad = 24;
    if (parent.y && (rect.top < box.top + pad || rect.bottom > box.bottom - pad)) {
      parent.node.scrollTop += rect.top - box.top - (parent.node.clientHeight - rect.height) / 2;
    }
    if (parent.x && (rect.left < box.left + pad || rect.right > box.right - pad)) {
      parent.node.scrollLeft += rect.left - box.left - (parent.node.clientWidth - rect.width) / 2;
    }
  };

  let lastKeyWasTab = false;
  document.addEventListener('keydown', (e) => { lastKeyWasTab = e.key === 'Tab' || e.key.startsWith('Arrow'); }, true);
  document.addEventListener('mousedown', () => { lastKeyWasTab = false; }, true);

  document.addEventListener('focusin', (event) => {
    const el = event.target;
    if (!(el instanceof HTMLElement)) return;
    for (let node = el.closest('.reveal:not(.reveal_visible)'); node; node = node.parentElement?.closest('.reveal:not(.reveal_visible)')) {
      node.classList.remove('reveal_pending');
      node.classList.add('reveal_visible');
    }
    if (lastKeyWasTab) requestAnimationFrame(() => bringIntoView(el));
  });

  /* ---------- Optional: Tab to images and content blocks ----------
     Theme settings > Accessibility > "Keyboard focus on images and content".
     Images get role="img" + their alt text; text blocks are read as they are.
     Skipped: anything already a link/button or holding one, hidden duplicates
     (marquee copies, aria-hidden, inert), header and footer. */
  const CONTENT_IMAGES = [
    '.bz-block-image', '.bz-mq__item', '.bz-brands__item', '[data-tab-media]', '.bz-product-highlight__layer',
    '.bz-footer__badge', '.payment-list__item',
    // product page: ingredient photo
    '.bz_ing_shot',
  ].join(', ');
  const CONTENT_TEXT = [
    // a heading block = caption pill + title, each its own stop
    '.bz-heading__caption', '.bz-heading__title-wrap',
    '.bz-description', '.bz-icon-text', '.bz-product-highlight__copy',
    // testimonials: "Verified buyer" + stars
    '.bz-ts__verified',
    // footer: promise row, address line
    '.bz-footer__promise-item', '.bz-footer__contact > li',
    // product page: ingredient percent + name, description, badge
    '.bz_ing_meta', '.bz_ing_copy', '.bz_ing_tag',
    // nutrition heading, offer banner
    '.bz_nutri_copy', '.bz_nutri_offer',
    // subscription terms and plan note (radios are native tab stops)
    '.bz_sub_term', '.bz_sub_note',
  ].join(', ');
  // Content cards that also hold a small control (info button). The card is still its own stop.
  const CONTENT_REGIONS = ['.bz_nutri_card'].join(', ');
  const CONTROL = 'a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';

  const makeContentFocusable = (root) => {
    if (window.themeSettings?.focusContent === false) return;
    const scope = root?.querySelectorAll ? root : document.body;
    if (!scope) return;
    const apply = (el, isImage) => {
      if (el.hasAttribute('data-a11y-content') || el.hasAttribute('tabindex')) return;
      if (el.closest('a, button, header, .bz_mega_drawer, search-modal, bz-search-modal, bz-cart-sidebar, [role="dialog"], [aria-hidden="true"], [inert], [hidden], .bz-ff__source, [data-inside-source]')) return;
      if (el.querySelector(CONTROL)) return;
      if (el.parentElement?.closest('[data-a11y-content]')) return;
      if (isImage) {
        const img = el.querySelector('img, svg');
        if (!img) return;
        const label = (img.getAttribute('alt') || img.getAttribute('aria-label') || img.querySelector('title')?.textContent || '').trim();
        el.setAttribute('role', 'img');
        el.setAttribute('aria-label', label || 'Image');
      } else if (!el.textContent.trim()) {
        return;
      }
      el.setAttribute('tabindex', '0');
      el.setAttribute('data-a11y-content', isImage ? 'image' : 'text');
    };
    scope.querySelectorAll(CONTENT_IMAGES).forEach((el) => apply(el, true));
    scope.querySelectorAll(CONTENT_TEXT).forEach((el) => apply(el, false));
    scope.querySelectorAll(CONTENT_REGIONS).forEach((el) => {
      if (el.hasAttribute('data-a11y-content') || el.hasAttribute('tabindex')) return;
      if (el.closest('header, .bz_mega_drawer, search-modal, bz-search-modal, bz-cart-sidebar, [role="dialog"], [aria-hidden="true"], [inert], [hidden]')) return;
      if (!el.textContent.trim()) return;
      el.setAttribute('tabindex', '0');
      el.setAttribute('data-a11y-content', 'text');
    });
  };

  const runContentFocus = (root) => requestAnimationFrame(() => makeContentFocusable(root));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => runContentFocus());
  else runContentFocus();
  window.addEventListener('load', () => runContentFocus());
  document.addEventListener('shopify:section:load', (e) => runContentFocus(e.target));
  window.addEventListener('bz:slider:ready', () => runContentFocus());

  window.BzA11y = { FOCUSABLE, isVisible, getFocusable, trapFocus, releaseFocus, makeContentFocusable };
})();
